/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from "react";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridClasses,
} from "@mui/x-data-grid";
import { Box } from "@mui/material";
import DisplayAmount from "../../shared/DisplayAmount";
import { ImSpinner8 } from "react-icons/im";

type RowData = {
  asset: string;
  available: string;
  pending: string;
  total: string;
  crypto: boolean;
};

const AccountBalancesTab: React.FC<{
  data: any;
  isFetchingBalance: boolean;
}> = ({ data, isFetchingBalance }) => {
  const [balancesData, setBalancesData] = useState<any>(null);
  const [fiatData, setFiatData] = useState<RowData[]>([]);
  const [cryptoData, setCryptoData] = useState<RowData[]>([]);

  useEffect(() => {
    setBalancesData(data);

    if (data && Array.isArray(data)) {
      const fiat = data.filter((item: RowData) => !item.crypto);
      const crypto = data.filter((item: RowData) => item.crypto);

      setFiatData(fiat);
      setCryptoData(crypto);
    }
  }, [data]);

  const columns: GridColDef[] = [
    {
      field: "asset",
      headerName: "Asset",
      sortable: false,
      flex: 1,
      renderCell: (params: GridRenderCellParams<RowData, any, string>) =>
        params.row.asset,
    },
    {
      field: "available",
      headerName: "Available",
      sortable: false,
      flex: 1,
      renderCell: (params: GridRenderCellParams<RowData, any, string>) => (
        <DisplayAmount
          amount={Number(params.row.available)}
          currency={params.row.asset}
        />
      ),
    },
    {
      field: "pending",
      headerName: "Pending",
      sortable: false,
      flex: 1,
      renderCell: (params: GridRenderCellParams<RowData, any, string>) => (
        <DisplayAmount
          amount={Number(params.row.pending)}
          currency={params.row.asset}
        />
      ),
    },
    {
      field: "total",
      headerName: "Total",
      sortable: false,
      flex: 1,
      renderCell: (params: GridRenderCellParams<RowData, any, string>) => (
        <DisplayAmount
          amount={Number(params.row.total)}
          currency={params.row.asset}
        />
      ),
    },
  ];

  const columnsCrypto: GridColDef[] = [
    {
      field: "asset",
      headerName: "Asset",
      sortable: false,
      flex: 1,
      renderCell: (params: GridRenderCellParams<RowData, any, string>) =>
        params.row.asset,
    },
    {
      field: "available",
      headerName: "Available",
      sortable: false,
      flex: 1,
      renderCell: (params: GridRenderCellParams<RowData, any, string>) =>
        params.row.available,
    },
    {
      field: "pending",
      headerName: "Pending",
      sortable: false,
      flex: 1,
      renderCell: (params: GridRenderCellParams<RowData, any, string>) =>
        params.row.pending,
    },
    {
      field: "total",
      headerName: "Total",
      sortable: false,
      flex: 1,
      renderCell: (params: GridRenderCellParams<RowData, any, string>) =>
        params.row.total,
    },
  ];

  const customDataGridStyles: Partial<GridClasses> = {
    root: "mui-table-custom",
    columnHeader: "text-start text-md",
    row: "",
  };

  const dataGridSx = {
    "& .MuiDataGrid-cell": {
      padding: "8px 16px",
    },
    "& .MuiDataGrid-columnHeader": {
      padding: "8px 16px",
    },
    "& .MuiTablePagination-input": {
      marginRight: "-20px",
    },
    "& .MuiTablePagination-displayedRows": {
      display: "none",
    },
  };

  if (isFetchingBalance || !balancesData) {
    return (
      <div className="flex min-h-[200px] items-center justify-center p-7">
        <ImSpinner8
          className="spinner mt-1 text-primary-500"
          style={{ fontSize: "1.8rem" }}
        />
      </div>
    );
  }

  if (
    !isFetchingBalance &&
    (!fiatData || fiatData.length === 0) &&
    (!cryptoData || cryptoData.length === 0)
  ) {
    return (
      <div className="p-7">
        <p className="py-8 text-center text-sm text-gray-500">
          No available balance
        </p>
      </div>
    );
  }

  return (
    <div className="px-4 py-8">
      {fiatData && fiatData.length > 0 && (
        <Box mb={4}>
          <div className="mb-2 text-sm font-semibold text-navy-700">
            Fiat Balances
          </div>
          <DataGrid
            sx={dataGridSx}
            loading={false}
            rows={fiatData}
            columns={columns}
            getRowId={(row) => row.asset}
            pageSizeOptions={[10, 25, 50, 100]}
            rowHeight={48}
            columnHeaderHeight={48}
            disableRowSelectionOnClick
            disableColumnFilter
            disableColumnMenu
            classes={customDataGridStyles}
            hideFooter={true}
            autoHeight
            localeText={{
              noRowsLabel: "No data available",
            }}
          />
        </Box>
      )}
      {cryptoData && cryptoData.length > 0 && (
        <Box>
          <div className="mb-2 text-sm font-semibold text-navy-700">
            Wallet Balances
          </div>
          <DataGrid
            sx={dataGridSx}
            loading={false}
            rows={cryptoData}
            columns={columnsCrypto}
            getRowId={(row) => row.asset}
            pageSizeOptions={[10, 25, 50, 100]}
            rowHeight={48}
            columnHeaderHeight={48}
            disableRowSelectionOnClick
            disableColumnFilter
            disableColumnMenu
            classes={customDataGridStyles}
            hideFooter={true}
            autoHeight
            localeText={{
              noRowsLabel: "No data available",
            }}
          />
        </Box>
      )}
    </div>
  );
};

export default AccountBalancesTab;
